import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/checkout/status/${sessionId}`
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          return;
        }

        if (response.data.status === "expired") {
          setStatus("error");
          return;
        }

        // Continue polling if still pending
        if (pollAttempts < 5) {
          setTimeout(() => {
            setPollAttempts((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Failed to check payment status:", error);
        if (pollAttempts < 5) {
          setTimeout(() => {
            setPollAttempts((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollAttempts]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-ff-bg flex items-center justify-center px-4"
    >
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 size={48} className="mx-auto text-ff-accent animate-spin mb-6" />
            <h1 className="font-serif text-2xl text-ff-text mb-4">
              Confirming your payment...
            </h1>
            <p className="text-ff-text-muted font-light">
              Please wait while we verify your subscription.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
            <h1 className="font-serif text-3xl text-ff-text mb-4">
              Welcome, Patron!
            </h1>
            <p className="text-ff-text-muted font-light mb-8">
              Thank you for supporting Freddy Fiction. You now have access to all
              patron benefits including early access to new series, comments, and
              behind-the-scenes content.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/library" className="btn-primary">
                Start Reading
              </Link>
              <Link to="/" className="btn-secondary">
                Back to Home
              </Link>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle size={64} className="mx-auto text-red-500 mb-6" />
            <h1 className="font-serif text-2xl text-ff-text mb-4">
              Something went wrong
            </h1>
            <p className="text-ff-text-muted font-light mb-8">
              We couldn't confirm your payment. If you believe this is an error,
              please contact support or try again.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/subscribe" className="btn-primary">
                Try Again
              </Link>
              <Link to="/" className="btn-secondary">
                Back to Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
